import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Plus, ClipboardList, AlertTriangle, RotateCw, Loader2, Building2, Stethoscope, UserRound } from 'lucide-react';
import CheckSetCard from '../components/checks/CheckSetCard';
import CheckSetEditor from '../components/checks/CheckSetEditor';
import useSetActions from '../components/checks/useSetActions';
import CheckSetsTab from '../components/doctorTabs/CheckSetsTab';
import SearchableSelect from '../components/common/SearchableSelect';
import { fetchCheckSets } from '../redux/checkSetSlice';
import { getDoctorNameList } from '../redux/doctorSlice';
import { groupByMeantFor } from '../utils/checkSets';
import { serverReason } from '../components/patientTabs/prescriptionHelpers';

// ADMIN → HOSPITAL CHECK SETS. The hospital's own sets, which every doctor of
// the hospital sees under "Hospital sets" (and may copy). Built by the admin,
// or by staff whose role has `checks.manage`. Any fields, any speciality — an
// optional free "Meant for" label only groups the list; it is not tied to any
// master (owner, 26 Sep 2026).
//
// The ADMIN also manages any doctor's OWN sets (spec §4, contract §3 — parity
// with the prescription-field rule): "Doctors' sets" → pick a doctor → the
// doctor's Settings → Check sets screen, acting on that doctor (their switch,
// their sets, "Copy to this doctor's sets"). Staff with checks.manage get the
// hospital's sets only; the server refuses them a doctor's (STAFF_TENANT_SETS_ONLY),
// so the choice is not drawn for them.
//
// The route is wrapped in TenantFeatureRoute("check_sets") and, for staff,
// PermissionGuard("checks.manage"); the server enforces both.

const SCOPES = [
    { key: 'hospital', icon: Building2, labelKey: 'scope_hospital' },
    { key: 'doctor', icon: Stethoscope, labelKey: 'scope_doctors' },
];

function HospitalCheckSets() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const isAdmin = (typeof localStorage !== 'undefined' && localStorage.getItem('Role')) === 'admin';
    const { manage, busyId } = useSelector((state) => state.checkSets);
    const { doctorNameList, loadingList } = useSelector((state) => state.doctor);
    const { remove } = useSetActions();
    const [editor, setEditor] = useState({ open: false, set: null });
    const [scope, setScope] = useState('hospital');
    const [doctorId, setDoctorId] = useState('');
    const doctorScope = isAdmin && scope === 'doctor';

    // The hospital's list; the doctor's list is CheckSetsTab's to ask for.
    useEffect(() => {
        if (!doctorScope) dispatch(fetchCheckSets({ purpose: 'manage' }));
    }, [dispatch, doctorScope]);

    // The doctors of this hospital — the same list the appointment list's
    // picker uses — asked each time the admin opens "Doctors' sets" (so a
    // failed read is retried by opening it again).
    const [askedDoctors, setAskedDoctors] = useState(false);
    const chooseScope = (key) => {
        if (!isAdmin) return;
        setScope(key);
        if (key === 'doctor' && scope !== 'doctor') {
            setAskedDoctors(true);
            dispatch(getDoctorNameList());
        }
    };

    const doctors = Array.isArray(doctorNameList) ? doctorNameList : [];
    const picked = doctors.find((d) => String(d.doctorId) === String(doctorId)) || null;
    const doctorOptions = doctors.map((d) => ({ value: d.doctorId, label: d.name || t('common.doctor_label', 'Doctor') }));

    // The hospital's list on screen only when the store holds it (it holds one
    // list at a time — a doctor's, when the admin just looked at one).
    const current = manage.purpose === 'manage' && manage.doctorId === null;
    const loaded = current && manage.loaded;
    const loadError = current ? manage.error : null;
    const groups = groupByMeantFor(manage.hospital);

    return (
        <div data-testid="hospital-check-sets-page" className="mx-auto rounded-2xl border border-gray-100 bg-white p-4 shadow-xl sm:p-6">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <h1 className="flex items-center gap-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-2xl font-bold text-transparent sm:text-3xl">
                        <ClipboardList className="h-7 w-7 text-[#4ABDBD]" aria-hidden="true" />
                        {t('checkSets.hospital_page_title')}
                    </h1>
                    <p className="mt-1 max-w-3xl text-sm text-gray-500">
                        {doctorScope ? t('checkSets.doctors_scope_subheading') : t('checkSets.hospital_page_subheading')}
                    </p>
                </div>
                {!doctorScope && (
                    <button
                        type="button"
                        data-testid="hospital-check-sets-new"
                        onClick={() => setEditor({ open: true, set: null })}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-4 py-2 text-sm font-semibold text-white shadow-lg transition-all hover:shadow-xl"
                    >
                        <Plus className="h-4 w-4" aria-hidden="true" />
                        {t('checkSets.new_set')}
                    </button>
                )}
            </div>

            {isAdmin && (
                <div
                    role="tablist"
                    aria-label={t('checkSets.scope_label')}
                    data-testid="hospital-check-sets-scope"
                    className="mb-6 flex w-full flex-wrap gap-1 rounded-2xl border border-gray-200 bg-gray-50 p-1 sm:w-fit"
                >
                    {SCOPES.map(({ key, icon, labelKey }) => {
                        const Icon = icon;
                        const on = scope === key;
                        return (
                            <button
                                key={key}
                                type="button"
                                role="tab"
                                aria-selected={on}
                                data-testid={`hospital-check-sets-scope-${key}`}
                                onClick={() => chooseScope(key)}
                                className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold transition-colors sm:flex-none ${on
                                    ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white shadow'
                                    : 'text-gray-600 hover:bg-white hover:text-[#2D8A8A]'}`}
                            >
                                <Icon className="h-4 w-4" aria-hidden="true" />
                                {t(`checkSets.${labelKey}`)}
                            </button>
                        );
                    })}
                </div>
            )}

            {doctorScope && (
                <div data-testid="hospital-check-sets-doctor-scope" className="space-y-6">
                    <div className="flex flex-col gap-1.5 sm:max-w-sm">
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">{t('checkSets.doctor_picker_label')}</span>
                        <SearchableSelect
                            testId="hospital-check-sets-doctor"
                            value={doctorId}
                            onChange={(v) => setDoctorId(v || '')}
                            options={doctorOptions}
                            placeholder={loadingList ? t('checkSets.loading') : t('checkSets.doctor_picker_placeholder')}
                            searchPlaceholder={t('checkSets.doctor_picker_search')}
                            icon={UserRound}
                            disabled={loadingList}
                            variant="form"
                        />
                    </div>
                    {picked ? (
                        <CheckSetsTab key={picked.doctorId} doctor={{ doctorId: picked.doctorId, name: picked.name }} />
                    ) : (
                        <div data-testid="hospital-check-sets-pick-doctor" className="mx-auto max-w-xl rounded-2xl border border-dashed border-gray-200 p-10 text-center">
                            <Stethoscope className="mx-auto mb-3 h-10 w-10 text-[#9DB3B1]" aria-hidden="true" />
                            <p className="text-sm text-gray-500">
                                {!loadingList && askedDoctors && doctors.length === 0 ? t('checkSets.no_doctors') : t('checkSets.pick_doctor_hint')}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {!doctorScope && manage.loading && !loaded && (
                <div data-testid="hospital-check-sets-loading" className="flex items-center justify-center gap-2 py-10 text-sm text-[#4ABDBD]">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    {t('checkSets.loading')}
                </div>
            )}

            {!doctorScope && loadError && (
                <div role="alert" data-testid="hospital-check-sets-error" className="mx-auto flex max-w-xl flex-col items-center gap-2 rounded-2xl border border-red-100 bg-red-50 p-6 text-center">
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-red-700">
                        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                        {serverReason(loadError, t('checkSets.load_failed'), i18n.language)}
                    </p>
                    <button
                        type="button"
                        data-testid="hospital-check-sets-retry"
                        onClick={() => dispatch(fetchCheckSets({ purpose: 'manage' }))}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                    >
                        <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('checkSets.retry')}
                    </button>
                </div>
            )}

            {!doctorScope && loaded && !loadError && (manage.hospital.length === 0 ? (
                <div data-testid="hospital-check-sets-empty" className="mx-auto max-w-xl rounded-2xl border border-dashed border-gray-200 p-10 text-center">
                    <ClipboardList className="mx-auto mb-3 h-10 w-10 text-[#9DB3B1]" aria-hidden="true" />
                    <p className="text-sm text-gray-500">{t('checkSets.no_hospital_sets_admin')}</p>
                </div>
            ) : (
                <div className="space-y-6" data-testid="hospital-check-sets-list">
                    {groups.map((g) => (
                        <section key={g.label || '_general'} data-testid={`hospital-check-sets-group-${g.label || 'general'}`}>
                            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#2D8A8A]">{g.label || t('checkSets.general_group')}</h2>
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                                {g.sets.map((set) => (
                                    <CheckSetCard
                                        key={set.setId}
                                        set={set}
                                        busy={busyId === set.setId}
                                        onEdit={(s) => setEditor({ open: true, set: s })}
                                        onDelete={remove}
                                    />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            ))}

            <CheckSetEditor
                isOpen={editor.open && !doctorScope}
                ownerType="tenant"
                set={editor.set}
                onClose={() => setEditor({ open: false, set: null })}
            />
        </div>
    );
}

export default HospitalCheckSets;
